/**
 * Set Password Page
 * For first-time password setup or password reset
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './LoginPage.css';

function SetPasswordPage() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    
    const { setPassword: setPasswordApi } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const token = searchParams.get('token');

    // Validate token on mount
    useState(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    // Password requirements
    const requirements = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        digit: /[0-9]/.test(password),
    };

    const allRequirementsMet = Object.values(requirements).every(Boolean);
    const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!allRequirementsMet) {
            setError('Password does not meet all requirements');
            return;
        }

        if (!passwordsMatch) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            await setPasswordApi(token, password);
            setSuccess(true);
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <h1>✓ Password Set</h1>
                        <p>Your password has been set successfully</p>
                    </div>
                    
                    <div className="alert alert-success">
                        You can now login with your email and new password.
                        Redirecting to login...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>Set Your Password</h1>
                    <p>Create a secure password for your account</p>
                </div>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Create a password"
                            required
                            autoComplete="new-password"
                        />
                        
                        <div className="password-requirements">
                            <p style={{ fontWeight: 500, marginBottom: '8px' }}>Password Requirements:</p>
                            <ul>
                                <li className={requirements.length ? 'valid' : 'invalid'}>
                                    ✓ At least 8 characters
                                </li>
                                <li className={requirements.uppercase ? 'valid' : 'invalid'}>
                                    ✓ One uppercase letter (A-Z)
                                </li>
                                <li className={requirements.lowercase ? 'valid' : 'invalid'}>
                                    ✓ One lowercase letter (a-z)
                                </li>
                                <li className={requirements.digit ? 'valid' : 'invalid'}>
                                    ✓ One number (0-9)
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm your password"
                            required
                            autoComplete="new-password"
                            style={{
                                borderColor: confirmPassword.length > 0 
                                    ? (passwordsMatch ? '#16a34a' : '#dc2626') 
                                    : '#e5e7eb'
                            }}
                        />
                    </div>

                    <button 
                        type="submit" 
                        className="btn btn-primary btn-full"
                        disabled={loading || !allRequirementsMet || !passwordsMatch}
                    >
                        {loading ? 'Setting Password...' : 'Set Password'}
                    </button>
                </form>

                <div className="login-footer">
                    <p>
                        <Link to="/login" className="link-button">
                            ← Back to Login
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default SetPasswordPage;
